// phase-compiler-six-tabs — the selected document's per-document detail, shown
// on the Documents tab BELOW the list. It gathers the controls the old
// right-pane Inspector carried on its "Document" tier (Placement,
// Classification approval) plus the document bits of the retired "Operations"
// tab (DOCX conversion, rationale-required section override) into one column of
// plain <details> disclosures — never a nested tab row. Preview/Compare/
// Annotations moved to the Review tab; Lifecycle/Navigation/Validation are
// their own main tabs, so none of those live here.
//
// CompilerPage owns selection, permissions, the section option list and the
// reload/toast callbacks; this component only arranges the existing canonical
// controls and forwards their success/error to the page's one toast surface.

import { type CSSProperties, type ReactElement } from 'react';
import type { Document } from '@/api/v2Client';
import { docReviewFlags } from '@/lib/docReviewFlags';
import { PlacementTab, type SectionOption } from './inspector/PlacementTab';
import { ClassificationTab } from './inspector/ClassificationTab';
import { InspectorOverrideSection } from './InspectorOverrideSection';
import { ConversionPanel } from '@/components/documents/ConversionPanel';
import type { ToastMessage } from '@/components/shared/PageToast';

export interface DocumentDetailSectionsProps {
  readonly document: Document;
  readonly sections: readonly SectionOption[];
  /** Documents:write (and not locked / not sequence-locked). */
  readonly canEdit: boolean;
  /** Documents:sign — gates the classification-approval sign-off. */
  readonly canApprove: boolean;
  readonly onDocumentUpdated: () => void;
  readonly onToast: (toast: ToastMessage) => void;
}

const wrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 16,
  borderTop: '1px solid var(--border)',
  paddingTop: 12,
};

const disclosure: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--surface)',
};

const summary: CSSProperties = {
  cursor: 'pointer',
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text)',
  userSelect: 'none',
};

const body: CSSProperties = { padding: '0 10px 10px 10px' };

/**
 * The selected document's detail disclosures for the Documents tab. Renders
 * nothing structural of its own beyond the disclosures — ConversionPanel
 * self-gates to null for native PDFs, so its disclosure is only shown when it
 * has something to say.
 */
export function DocumentDetailSections({
  document,
  sections,
  canEdit,
  canApprove,
  onDocumentUpdated,
  onToast,
}: DocumentDetailSectionsProps): ReactElement {
  const conversionStatus =
    (document.ConversionStatus as 'pending' | 'success' | 'failed' | null | undefined) ?? null;

  return (
    <div data-testid="document-detail-sections" style={wrap}>
      <details open style={disclosure} data-testid="document-detail-placement">
        <summary style={summary}>Placement</summary>
        <div style={body}>
          <PlacementTab
            documentId={document.Id}
            currentSectionCode={(document.CtdSectionCode as string | null | undefined) ?? null}
            sections={sections}
            canEdit={canEdit}
            onSuccess={(newSectionCode) => {
              onToast({ type: 'success', text: `Placement saved to ${newSectionCode}.` });
              onDocumentUpdated();
            }}
            isConfirmed={
              !docReviewFlags({ ...document, ConfirmedAt: document.ConfirmedAt ?? null }).unconfirmed
            }
            classifierConfidence={document.ClassificationConfidence ?? null}
            suggestedSectionCode={(document.Classification as string | null | undefined) ?? null}
          />
        </div>
      </details>

      <details style={disclosure} data-testid="document-detail-classification">
        <summary style={summary}>Classification</summary>
        <div style={body}>
          <ClassificationTab
            documentId={document.Id}
            rowVersion={document.RowVersion}
            classifierSuggestion={(document.Classification as string | null | undefined) ?? null}
            classifierConfidence={
              (document.ClassificationConfidence as number | null | undefined) ?? null
            }
            currentApprovedValue={
              (document.ClassificationApprovedValue as string | null | undefined) ?? null
            }
            currentApprovedAt={
              (document.ClassificationApprovedAt as string | null | undefined) ?? null
            }
            currentApprovedByEmail={(document.ConfirmedBy as string | null | undefined) ?? null}
            sections={sections}
            canApprove={canApprove}
            onSuccess={(approved) => {
              onToast({ type: 'success', text: `Classification approved as ${approved}.` });
              onDocumentUpdated();
            }}
            layer1Proposal={{
              status: document.Layer1ProposalStatus ?? null,
              family: document.Layer1ProposedFamily ?? null,
              section: document.Layer1ProposedSection ?? null,
              supportCount: document.Layer1ProposalSupportCount ?? null,
            }}
          />
        </div>
      </details>

      {/* DOCX conversion — self-gates to null for native PDFs / pre-feature rows,
          so the disclosure is only meaningful when a conversion exists. */}
      {conversionStatus !== null && (
        <details style={disclosure} data-testid="document-detail-conversion">
          <summary style={summary}>Conversion</summary>
          <div style={body}>
            <ConversionPanel
              documentId={document.Id}
              fileName={document.FileName}
              conversionStatus={conversionStatus}
              canEdit={canEdit}
              onReconverted={() => {
                onToast({ type: 'success', text: 'Reconvert succeeded — PDF is ready.' });
                onDocumentUpdated();
              }}
              onError={(message) => onToast({ type: 'error', text: message })}
            />
          </div>
        </details>
      )}

      {/* Rationale-required section override (reclassification with an audited
          reason) — distinct from the plain Placement save above. */}
      <details style={disclosure} data-testid="document-detail-override">
        <summary style={summary}>Section override</summary>
        <div style={body}>
          <InspectorOverrideSection
            documentId={document.Id}
            currentSectionCode={(document.CtdSectionCode as string | null | undefined) ?? null}
            sections={sections}
            canEdit={canEdit}
            onSuccess={(newSection) => {
              onToast({ type: 'success', text: `Section override saved (${newSection}).` });
              onDocumentUpdated();
            }}
          />
        </div>
      </details>
    </div>
  );
}

export default DocumentDetailSections;
